import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Play, Users } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import { createChallenge, LinkStep } from "./WagerPicker";
import { shootoutApi } from "../lib/api";
import { PEP } from "../data/gameData";

const StakeOption = ({ s, active, disabled, onPick }) => (
  <button data-testid={`stake-option-${s}`} onClick={() => onPick(s)} disabled={disabled}
    className={`rounded-xl px-3 py-3 border text-left transition-colors disabled:opacity-35 ${active ? "bg-[#10B981] text-[#0F172A] border-[#10B981]" : "bg-white/5 text-white border-white/10 hover:border-white/25"}`}>
    <span className="block font-display font-extrabold text-lg leading-none">£{s}</span>
    <span className={`block text-xs font-semibold mt-1 ${active ? "text-[#0F172A]/80" : "text-[#10B981]"}`}>Win £{s * 2}</span>
  </button>
);

// Home game pop-up: choose your stake, then play Pep or send a mate a challenge link.
const PepPicker = ({ open, game, user, onClose }) => {
  const navigate = useNavigate();
  const coins = user?.coins ?? 0;
  const [stake, setStake] = useState(null);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const [created, setCreated] = useState(null);

  useEffect(() => {
    if (!open || !game) return;
    setCreated(null); setError(null); setBusy(null);
    setStake(game.stakes.find((s) => s <= coins) ?? 0);
  }, [open, game, coins]);

  if (!game) return null;

  const run = async (kind) => {
    if (busy || stake === null) return;
    setBusy(kind);
    setError(null);
    try {
      if (kind === "pep") {
        const to = game.id === "draft" ? `/pep?stake=${stake}` : `/p/${(await shootoutApi.pep(stake)).shootout.id}`;
        navigate(to, { replace: game.id === "draft" });
      } else {
        setCreated(await createChallenge(game, stake));
      }
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't start the game, try again");
    }
    setBusy(null);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="stake-picker-modal" className="theme-light bg-[#1E293B] border border-[#10B981]/30 text-white w-[calc(100%-2rem)] max-w-sm p-6 rounded-2xl">
        <DialogTitle data-testid="stake-picker-title" className="font-display font-extrabold uppercase text-2xl">{game.title}</DialogTitle>
        <DialogDescription className="text-slate-400 text-sm">{created ? "Share it however you like." : "Choose your stake. Win and you double it."}</DialogDescription>
        {created ? (
          <div className="mt-3 min-w-0"><LinkStep game={game} {...created} onOpen={(to) => navigate(to)} /></div>
        ) : (
          <>
            <div className="mt-4 text-[11px] label-eyebrow text-[#94A3B8]">Choose your stake</div>
            <div className={`mt-2 grid gap-2 ${game.stakes.length === 4 ? "grid-cols-2" : "grid-cols-3"}`}>
              {game.stakes.map((s) => <StakeOption key={s} s={s} active={stake === s} disabled={s > coins} onPick={setStake} />)}
            </div>
            {stake === 0 && (
              <div data-testid="stake-free-note" className="mt-3 rounded-xl border border-[#38BDF8]/30 bg-[#38BDF8]/10 p-3 text-sm">
                <div className="font-semibold text-[#38BDF8]">Not enough to stake · you can still play Pep for free</div>
              </div>
            )}
            <div className="mt-5 space-y-2.5">
              <button data-testid="stake-play-pep-btn" onClick={() => run("pep")} disabled={!!busy}
                className="btn-green w-full rounded-xl px-4 py-3.5 flex items-center gap-3 text-left disabled:opacity-50">
                <img src={PEP.avatar} alt={PEP.name} className="w-9 h-9 rounded-full object-cover border border-black/20 shrink-0" />
                <span className="flex-1 font-display font-extrabold uppercase text-lg leading-none">Play {PEP.name}</span>
                {busy === "pep" ? <Loader2 className="w-5 h-5 animate-spin" /> : <Play className="w-5 h-5" />}
              </button>
              <button data-testid="stake-wager-mate-btn" onClick={() => run("mate")} disabled={!!busy || !stake}
                className="w-full rounded-xl px-4 py-3.5 flex items-center gap-3 text-left text-white border border-[#334155] bg-[#0F172A] hover:border-[#475569] transition-colors disabled:opacity-50">
                <span className="w-9 h-9 rounded-full bg-[#10B981]/15 flex items-center justify-center shrink-0"><Users className="w-5 h-5 text-[#10B981]" /></span>
                <span className="flex-1 font-display font-extrabold uppercase text-lg leading-none">Wager a mate</span>
                {busy === "mate" && <Loader2 className="w-5 h-5 animate-spin" />}
              </button>
            </div>
            {error && <p data-testid="stake-picker-error" className="text-[#EF4444] text-sm mt-2">{error}</p>}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PepPicker;
